import { AudioPosition } from '@/types/audioPosition';
import { authService } from '@/services/authService';
import {
  ServerProgress,
  ProgressSyncEntry,
  fetchAllProgress,
  putProgress,
  syncProgress,
} from '@/services/progressApi';
import { getCookie, setCookie, removeCookie, readAllEntries } from './cookieUtils';
import { Book } from './bookRouting';

const AUDIO_PROGRESS_COOKIE_DAYS = 365;

// Server-sync: cookies stay the local cache/offline fallback; for logged-in
// users they are mirrored to the server (debounced) so progress is durable and
// cross-device. See server/src/progress_routes.py.
const PROGRESS_PUSH_DEBOUNCE_MS = 30_000;
const PROGRESS_SYNCED_EVENT = 'aleph:progress-synced';

const pendingPushes = new Map<string, AudioPosition>();
let pushTimer: ReturnType<typeof setTimeout> | null = null;

// Shared prefix for every progress entry, canonical and legacy alike. The store
// is device-global rather than per-account, which is why sign-out has to empty
// it — see clearLocalProgress.
const PROGRESS_KEY_PREFIX = 'audioTime_';

/**
 * How many progress entries the local store keeps.
 *
 * These are written at `path=/`, so the browser attaches every one of them to
 * every same-origin request — the API ignores them (it authenticates with a
 * Bearer token), but they still cost request-header bytes, and the store grew
 * without any bound: a book visited once kept an entry for a year. Capacity
 * limits bite too, at ~180 cookies per domain on Chrome.
 *
 * 25 is comfortably more books than anyone has on the go at once, and for a
 * signed-in user nothing is actually lost — the server keeps every row, and
 * hydrateProgressFromServer writes back whatever is opened again.
 */
const MAX_PROGRESS_ENTRIES = 25;

/**
 * Generates the cookie key for a specific book's audio progress
 * @param bookKey - The unique identifier of the book
 * @returns The cookie key string
 */
export const getAudioProgressCookieKey = (bookKey: string): string => {
  return `${PROGRESS_KEY_PREFIX}${bookKey}`;
};

const parseAudioProgressPayload = (savedData: string, bookKey: string): AudioPosition | null => {
  const parseCandidates = [savedData];

  try {
    const decoded = decodeURIComponent(savedData);
    if (decoded !== savedData) {
      parseCandidates.push(decoded);
    }
  } catch {
    // Ignore decode errors and continue with raw value only.
  }

  for (const payload of parseCandidates) {
    try {
      const position = JSON.parse(payload) as AudioPosition;

      if (position.textFingerprint) {
        position.textFingerprint = position.textFingerprint.replace(/%3B/g, ';');
      }

      if (typeof position === 'object' && position !== null) {
        if (typeof position.timeOffset === 'number') return position;

        if (typeof position.totalProgress === 'number') {
          return {
            ...position,
            timeOffset: position.timeOffset || 0
          } as AudioPosition;
        }
      }
    } catch {
      // Keep trying alternate payload forms.
    }
  }

  const fallbackNumber = parseFloat(savedData);
  if (!Number.isNaN(fallbackNumber) && Number.isFinite(fallbackNumber)) {
    return {
      sentenceIndex: -1,
      textFingerprint: '',
      timeOffset: fallbackNumber,
      totalProgress: fallbackNumber
    };
  }

  console.log(bookKey, 'savedData:', savedData);
  return null;
};

// Reads every entry from the client store. Backed by document.cookie on web and
// by localStorage in the native shell (see cookieUtils.readAllEntries) — the
// latter is why cross-device progress now surfaces on iOS.
const readCookieEntries = (): Array<{ key: string; value: string }> => readAllEntries();

const normalizeLegacyCandidate = (value: string): string[] => {
  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  const candidates = new Set<string>();
  const lower = trimmed.toLowerCase();

  candidates.add(trimmed);
  candidates.add(lower);

  // Early key format used underscore slugs generated from title text.
  const underscoreSlug = lower
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  if (underscoreSlug) {
    candidates.add(underscoreSlug);
  }

  // Also try a hyphen slug variant and first-word fallback.
  const hyphenSlug = lower
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  if (hyphenSlug) {
    candidates.add(hyphenSlug);
  }

  const firstWord = lower.split(/\s+/)[0]?.replace(/[^a-z0-9]/g, '');
  if (firstWord) {
    candidates.add(firstWord);
  }

  return Array.from(candidates);
};

const findLegacyCookieMatch = (bookKey: string, legacyBookKeys: string[]): { key: string; value: string } | null => {
  const entries = readCookieEntries();
  if (entries.length === 0) {
    return null;
  }

  const candidateSet = new Set<string>(normalizeLegacyCandidate(bookKey));
  legacyBookKeys.flatMap(normalizeLegacyCandidate).forEach((candidate) => candidateSet.add(candidate));
  const candidates = Array.from(candidateSet);

  const matches: Array<{ key: string; value: string; progress: number; lastPlayed: number }> = [];
  for (const entry of entries) {
    for (const candidate of candidates) {
      const prefix = getAudioProgressCookieKey(candidate);
      if (entry.key === prefix || entry.key.startsWith(`${prefix}_`)) {
        const parsed = parseAudioProgressPayload(entry.value, candidate);
        if (parsed) {
          matches.push({
            key: entry.key,
            value: entry.value,
            progress: typeof parsed.totalProgress === 'number' ? parsed.totalProgress : parsed.timeOffset || 0,
            lastPlayed: parsed.lastPlayed || 0
          });
        }
        break;
      }
    }
  }

  if (matches.length === 0) {
    return null;
  }

  matches.sort((a, b) => {
    if (b.lastPlayed !== a.lastPlayed) {
      return b.lastPlayed - a.lastPlayed;
    }
    return b.progress - a.progress;
  });

  return {
    key: matches[0].key,
    value: matches[0].value
  };
};

export const getLegacyBookKeyCandidates = (book: Book): string[] => {
  return [book.slug, book.title].filter(Boolean);
};

export const getAudioProgressForBook = (book: Book): AudioPosition | null => {
  return getAudioProgress(book.id, getLegacyBookKeyCandidates(book));
};

/**
 * Centralized function to update and save audio progress to a cookie.
 * Fixed to preserve rating/isFavourite when updates omit them.
 * Also avoids mutating the original data passed in.
 * @param bookKey - The unique identifier of the book.
 * @param updates - A partial AudioPosition object with the fields to update.
 */
/**
 * Drop everything past the MAX_PROGRESS_ENTRIES most recently played books.
 *
 * Ranked by `lastPlayed` alone, so this is a hard cap: an entry that carries
 * only a rating, a favourite or a hidden flag has no timestamp, sorts to the
 * bottom and is evicted like any other. That is deliberate — a soft cap that
 * exempted flagged entries would not be a cap. Signed-in users get those flags
 * back from the server; an anonymous user does not.
 *
 * An entry whose payload will not parse also ranks as 0, which makes this the
 * one thing that clears out corrupt or half-written values.
 *
 * Only PROGRESS_KEY_PREFIX keys are considered — the same store holds auth and
 * theme values that must survive.
 */
const pruneProgressEntries = (): void => {
  const entries: Array<{ key: string; lastPlayed: number }> = [];

  for (const { key, value } of readCookieEntries()) {
    if (!key.startsWith(PROGRESS_KEY_PREFIX)) {
      continue;
    }
    const bookId = key.slice(PROGRESS_KEY_PREFIX.length);
    if (!bookId) {
      continue;
    }
    const position = parseAudioProgressPayload(value, bookId);
    entries.push({ key, lastPlayed: position?.lastPlayed || 0 });
  }

  if (entries.length <= MAX_PROGRESS_ENTRIES) {
    return;
  }

  // Newest first. Ties break on the key so that two entries sharing a timestamp
  // evict in the same order on every device rather than by store iteration.
  entries.sort((a, b) => (b.lastPlayed - a.lastPlayed) || a.key.localeCompare(b.key));

  for (const { key } of entries.slice(MAX_PROGRESS_ENTRIES)) {
    removeCookie(key);
  }
};

/**
 * Writes an AudioPosition to its canonical cookie (handles textFingerprint
 * encoding). This is the low-level cookie write with no server side-effects;
 * use it for server->cookie hydration so we don't echo a push back.
 */
const writeAudioProgressCookie = (cookieKey: string, position: AudioPosition): void => {
  const positionToSave = { ...position };

  if (positionToSave.textFingerprint) {
    positionToSave.textFingerprint = positionToSave.textFingerprint.replace(/;/g, '%3B');
  }

  setCookie(cookieKey, JSON.stringify(positionToSave), AUDIO_PROGRESS_COOKIE_DAYS);

  // After the write, so the entry just saved is ranked with the rest — being
  // the most recently played, it is the last thing that would ever be dropped.
  pruneProgressEntries();
};

/**
 * Queues a debounced server push for a book's progress. No-op for anonymous
 * users (cookie-only fallback). Coalesces per book and flushes every
 * PROGRESS_PUSH_DEBOUNCE_MS, plus on page-hide/unload (see listeners below).
 */
const enqueueServerPush = (bookKey: string, position: AudioPosition): void => {
  if (!authService.hasStoredCredentials()) {
    return;
  }

  pendingPushes.set(bookKey, position);

  if (!pushTimer) {
    pushTimer = setTimeout(() => {
      void flushProgressQueue();
    }, PROGRESS_PUSH_DEBOUNCE_MS);
  }
};

/**
 * Flushes any queued progress pushes to the server immediately. Safe to call
 * on pause, page-hide, or unmount. Resolves once all in-flight PUTs settle.
 */
export const flushProgressQueue = async (): Promise<void> => {
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }

  if (pendingPushes.size === 0) {
    return;
  }

  const entries = Array.from(pendingPushes.entries());
  pendingPushes.clear();

  if (!authService.hasStoredCredentials()) {
    return; // dropped on logout; cookies remain the local cache
  }

  await Promise.all(
    entries.map(([bookId, position]) =>
      putProgress(bookId, position).catch((error) => {
        console.warn('Failed to push progress for', bookId, error);
      })
    )
  );
};

const updateAndSaveAudioProgress = (bookKey: string, updates: Partial<AudioPosition>): void => {
  const key = getAudioProgressCookieKey(bookKey);
  const existingData = getAudioProgress(bookKey);

  const defaultPosition: AudioPosition = {
    sentenceIndex: -1,
    textFingerprint: '',
    timeOffset: 0,
    totalProgress: 0,
  };

  const mergedPosition: AudioPosition = {
    ...(existingData || defaultPosition),
    ...updates,
    rating: updates.rating !== undefined ? updates.rating : existingData?.rating,
    isFavourite: updates.isFavourite !== undefined ? updates.isFavourite : existingData?.isFavourite,
    isHidden: updates.isHidden !== undefined ? updates.isHidden : existingData?.isHidden
  };

  writeAudioProgressCookie(key, mergedPosition);
  enqueueServerPush(bookKey, mergedPosition);
};

/**
 * Saves the audio progress to a cookie.
 * Merges with existing data to preserve ratings/favourites if not provided in new position.
 * @param bookKey - The unique identifier of the book.
 * @param position - The audio position data to save.
 */
export const saveAudioProgress = (bookKey: string, position: AudioPosition): void => {
  updateAndSaveAudioProgress(bookKey, position);
};

/**
 * Saves the book rating to the audio progress cookie.
 * @param bookKey - The unique identifier of the book.
 * @param rating - The rating value (1-5).
 */
export const saveBookRating = (bookKey: string, rating: number): void => {
  updateAndSaveAudioProgress(bookKey, { rating });
};

/**
 * Saves the book favourite status to the audio progress cookie.
 * @param bookKey - The unique identifier of the book.
 * @param isFavourite - Whether the book is a favourite.
 */
export const saveBookFavourite = (bookKey: string, isFavourite: boolean): void => {
  updateAndSaveAudioProgress(bookKey, { isFavourite });
};

/**
 * Saves the book hidden status to the audio progress cookie.
 * @param bookKey - The unique identifier of the book.
 * @param isHidden - Whether the book is hidden.
 */
export const saveBookHidden = (bookKey: string, isHidden: boolean): void => {
  updateAndSaveAudioProgress(bookKey, { isHidden });
};

/**
 * Clears the audio progress cookie for a specific book
 * @param bookKey - The unique identifier of the book
 */
export const clearAudioProgress = (bookKey: string): void => {
  // Zero the resume progress while preserving rating/favourite/hidden, and stamp
  // lastPlayed so the cleared state wins last-write-wins on the server too.
  updateAndSaveAudioProgress(bookKey, {
    sentenceIndex: -1,
    textFingerprint: '',
    timeOffset: 0,
    totalProgress: 0,
    lastPlayed: Date.now(),
  });
};

/**
 * Retrieves and parses the audio progress from a cookie
 * Handles both new JSON format and legacy number format
 * @param bookKey - The unique identifier of the book
 * @returns The parsed AudioPosition or null if not found/invalid
 */
export const getAudioProgress = (bookKey: string, legacyBookKeys: string[] = []): AudioPosition | null => {
  const canonicalKey = getAudioProgressCookieKey(bookKey);
  let sourceKey = canonicalKey;
  let savedData = getCookie(canonicalKey);

  if (!savedData) {
    const legacyMatch = findLegacyCookieMatch(bookKey, legacyBookKeys);
    if (!legacyMatch) {
      return null;
    }
    sourceKey = legacyMatch.key;
    savedData = legacyMatch.value;
  }

  const parsed = parseAudioProgressPayload(savedData, bookKey);
  if (!parsed) {
    return null;
  }

  if (sourceKey !== canonicalKey) {
    // Same write as any other, so the copy is subject to the entry cap too.
    writeAudioProgressCookie(canonicalKey, parsed);
  }

  return parsed;
};

/**
 * Helper to get the most relevant time value for display or calculation
 * Prioritizes totalProgress, then falls back to timeOffset
 * @param position - The AudioPosition object
 * @returns The effective time value
 */
export const getEffectiveProgress = (position: AudioPosition | null): number => {
  if (!position) return 0;

  if (typeof position.totalProgress === 'number') {
    return position.totalProgress;
  }

  if (position.timeOffset > 0) {
    return position.timeOffset;
  }

  return 0;
};

/**
 * Calculates the progress percentage for a book based on saved cookies
 * @param book - The book object
 * @returns The progress percentage (0-100)
 */
export const calculateBookProgress = (book: Book): number => {
  let maxTime = 0;

  const progress = getEffectiveProgress(getAudioProgressForBook(book));
  if (progress > maxTime) maxTime = progress;

  if (maxTime > 0 && book.totalDuration > 0) {
    const progress = (maxTime / book.totalDuration) * 100;
    return Math.round(Math.min(progress, 100));
  }

  return 0;
};

/**
 * Checks if a book is considered "in progress" and should be shown in My Books
 * @param book - The book object
 * @param progress - Optional progress value. If not provided, it will be retrieved from cookies.
 * @returns boolean
 */
export const isBookInProgress = (book: Book, progress?: number): boolean => {
  const currentProgress = progress !== undefined ? progress : getEffectiveProgress(getAudioProgressForBook(book));
  return currentProgress > 0 && (book.totalDuration - currentProgress) > 0.125;
};

/**
 * Checks if a book is considered "finished"
 * @param book - The book object
 * @param progress - Optional progress value. If not provided, it will be retrieved from cookies.
 * @returns boolean
 */
export const isBookFinished = (book: Book, progress?: number): boolean => {
  const currentProgress = progress !== undefined ? progress : getEffectiveProgress(getAudioProgressForBook(book));
  return currentProgress > 0 && (book.totalDuration - currentProgress) <= 0.125;
};

// ---------------------------------------------------------------------------
// Server sync: hydrate (server -> cookie) and migrate (cookie -> server)
// ---------------------------------------------------------------------------

const PROGRESS_MIGRATION_FLAG_PREFIX = 'aleph_progress_migrated_';

/** Notify views (e.g. My Books) that cookies now reflect fresh server state. */
const notifyProgressSynced = (): void => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(PROGRESS_SYNCED_EVENT));
  }
};

/** Reconstruct the cookie AudioPosition from a server progress row. */
const serverRowToPosition = (row: ServerProgress): AudioPosition => {
  const state = row.state || {};
  return {
    sentenceIndex: typeof state.sentenceIndex === 'number' ? state.sentenceIndex : -1,
    textFingerprint: typeof state.textFingerprint === 'string' ? state.textFingerprint : '',
    timeOffset: typeof state.timeOffset === 'number' ? state.timeOffset : (row.total_progress || 0),
    totalProgress: row.total_progress || 0,
    lastPlayed: row.last_played ?? undefined,
    chapterId: state.chapterId,
    rating: state.rating,
    isFavourite: state.isFavourite,
    isHidden: state.isHidden,
  };
};

/** Merge one server row into its cookie. Server wins when its lastPlayed >= local. */
const mergeServerRowIntoCookie = (row: ServerProgress): void => {
  const local = getAudioProgress(row.book_id);
  const localLastPlayed = local?.lastPlayed || 0;
  const serverLastPlayed = row.last_played || 0;

  if (local && serverLastPlayed < localLastPlayed) {
    return; // local cookie is newer — keep it (it will be pushed up later)
  }

  writeAudioProgressCookie(getAudioProgressCookieKey(row.book_id), serverRowToPosition(row));
};

/** Collect local audioTime_* cookies as sync entries keyed by their book id. */
const collectLocalProgressEntries = (): ProgressSyncEntry[] => {
  const entries: ProgressSyncEntry[] = [];

  for (const { key, value } of readCookieEntries()) {
    if (!key.startsWith(PROGRESS_KEY_PREFIX)) {
      continue;
    }
    const bookId = key.slice(PROGRESS_KEY_PREFIX.length);
    if (!bookId) {
      continue;
    }
    const position = parseAudioProgressPayload(value, bookId);
    if (!position) {
      continue;
    }
    // Only seed entries that carry meaningful state.
    const hasProgress = (position.totalProgress || 0) > 0 || (position.timeOffset || 0) > 0;
    const hasFlags = position.rating !== undefined || position.isFavourite !== undefined || position.isHidden !== undefined;
    if (hasProgress || hasFlags) {
      entries.push({ book_id: bookId, ...position });
    }
  }

  return entries;
};

/**
 * Whether the store shows any book actually started (as opposed to merely
 * rated, favourited or hidden). Synchronous, so callers that need an answer
 * during render get one — but it only sees what the device already knows, so a
 * fresh install reads false until hydrateProgressFromServer has landed.
 */
export const hasAnyStartedBook = (): boolean => {
  for (const { key, value } of readCookieEntries()) {
    if (!key.startsWith(PROGRESS_KEY_PREFIX)) {
      continue;
    }
    const bookId = key.slice(PROGRESS_KEY_PREFIX.length);
    if (!bookId) {
      continue;
    }
    const position = parseAudioProgressPayload(value, bookId);
    if ((position?.totalProgress || 0) > 0 || (position?.timeOffset || 0) > 0) {
      return true;
    }
  }
  return false;
};

/**
 * Pull all server progress and merge it into cookies (server wins on ties).
 * Returns the rows it merged, so a caller that needs both the server's view and
 * a hydrated local store can have them off one request.
 */
export const hydrateProgressFromServer = async (): Promise<ServerProgress[]> => {
  if (!authService.hasStoredCredentials()) {
    return [];
  }
  const rows = await fetchAllProgress();
  rows.forEach(mergeServerRowIntoCookie);
  notifyProgressSynced();
  return rows;
};

/**
 * One-time per-device migration: push existing local cookies to the server and
 * merge the authoritative result back into cookies.
 */
export const migrateLocalProgressToServer = async (): Promise<void> => {
  if (!authService.hasStoredCredentials()) {
    return;
  }
  const merged = await syncProgress(collectLocalProgressEntries());
  merged.forEach(mergeServerRowIntoCookie);
  notifyProgressSynced();
};

// ---------------------------------------------------------------------------
// Store ownership
//
// Progress entries are keyed by book alone, so a single device store is shared
// by every account that signs in on it. Whoever the store currently belongs to
// is recorded here: a user id, or ANONYMOUS_OWNER for progress accumulated
// while signed out. Reading someone else's entries would show their library in
// My Books; uploading them during the cookie->server migration would write
// their history into the new account for good.
// ---------------------------------------------------------------------------

const PROGRESS_OWNER_KEY = 'aleph_progress_owner';
const ANONYMOUS_OWNER = 'anon';

const readProgressOwner = (): string | null => {
  try {
    return localStorage.getItem(PROGRESS_OWNER_KEY);
  } catch {
    return null;
  }
};

const writeProgressOwner = (owner: string): void => {
  try {
    localStorage.setItem(PROGRESS_OWNER_KEY, owner);
  } catch {
    // Storage unavailable — the store is then effectively per-session anyway.
  }
};

/**
 * Fallback for devices last used before the owner marker existed: the per-user
 * migration flags name every account that has ever synced here. One belonging
 * to somebody other than the user signing in means the store is that account's
 * data, not anonymous progress waiting to be claimed.
 */
const hasForeignMigrationFlag = (userId: string): boolean => {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(PROGRESS_MIGRATION_FLAG_PREFIX)) {
        continue;
      }
      if (key.slice(PROGRESS_MIGRATION_FLAG_PREFIX.length) !== userId) {
        return true;
      }
    }
  } catch {
    // Storage unavailable — no flags to read, treat the store as anonymous.
  }
  return false;
};

/**
 * Empty this device's progress store and mark it anonymous. Also drops queued
 * pushes, which are addressed to the outgoing account and must not be replayed
 * under the next one. Views re-read on the synced event, so My Books empties
 * immediately rather than at the next reload.
 */
export const clearLocalProgress = (): void => {
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
  pendingPushes.clear();

  for (const { key } of readCookieEntries()) {
    if (key.startsWith(PROGRESS_KEY_PREFIX)) {
      removeCookie(key);
    }
  }

  writeProgressOwner(ANONYMOUS_OWNER);
  notifyProgressSynced();
};

/**
 * Sign-out counterpart to initProgressSync: flush what the departing account
 * has pending, then empty the store. Must run while its token is still valid,
 * so call this before authService.logout().
 */
export const resetProgressForSignOut = async (): Promise<void> => {
  try {
    await flushProgressQueue();
  } catch (error) {
    console.warn('Failed to flush progress before sign-out', error);
  }
  clearLocalProgress();
};

/**
 * Reconcile cookies with the server for a logged-in user. Discards any store
 * left behind by a different account, runs the one-time cookie->server
 * migration on first login per device, then hydrates on every subsequent load.
 * Falls back silently to cookie-only on any failure.
 */
export const initProgressSync = async (userId: string): Promise<void> => {
  if (!authService.hasStoredCredentials()) {
    return;
  }

  // The owner marker and migration flags are strings (localStorage stores
  // nothing else), but the id arrives as the API sent it — a number, whatever
  // AuthUser declares. Compared raw, "2" !== 2 read every signed-in load as
  // another account's store and wiped it, leaving views that had just read it
  // on empty progress until hydration refilled it.
  const ownerId = String(userId);
  const owner = readProgressOwner();
  const belongsToAnotherAccount = owner !== null
    ? owner !== ANONYMOUS_OWNER && owner !== ownerId
    : hasForeignMigrationFlag(ownerId);

  if (belongsToAnotherAccount) {
    clearLocalProgress();
  }

  // Claim the store before syncing: on a failed sync it is still this user's
  // store, and the migration flag below governs whether the upload is retried.
  writeProgressOwner(ownerId);

  const migrationFlag = `${PROGRESS_MIGRATION_FLAG_PREFIX}${ownerId}`;
  let alreadyMigrated = false;
  try {
    alreadyMigrated = localStorage.getItem(migrationFlag) === '1';
  } catch {
    // localStorage unavailable — treat as not-yet-migrated and hydrate-only.
  }

  try {
    if (alreadyMigrated) {
      await hydrateProgressFromServer();
    } else {
      await migrateLocalProgressToServer();
      try {
        localStorage.setItem(migrationFlag, '1');
      } catch {
        // Ignore — worst case we re-run the idempotent migration next load.
      }
    }
  } catch (error) {
    console.warn('Progress sync failed; continuing with cookie-only progress', error);
  }
};

// Best-effort flush of queued pushes when the page is backgrounded or unloaded.
// keepalive on the PUT (see progressApi) lets these requests complete.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void flushProgressQueue();
    }
  });
}
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    void flushProgressQueue();
  });
}
